import { useMemo } from 'react';
import { t } from '@lingui/macro';
import { get } from 'lodash';
import Icon from '@material-ui/core/Icon';
import { makeStyles } from '@material-ui/core/styles';
import DataTable from '../../data-table/DataTable';
import { COLUMN_TYPES } from '../../data-table/DataTableColumns';
import { useQueryLoader } from '../../data-table/Loaders';
import { useIntervalRefreshTrigger } from '../../data-table/Triggers';
import { SELECTIONMODE_MULTI } from '../../data-table/SelectionContext';
import { SORT_DESC } from '../../../utilities/FormUtilities';
import { AdminPages, buildRootPageTarget } from '../AdminLinkUtils';
import { SERVICE_TESTS_LIST_URL } from './TestTypesAdminConstants';
import { formatScheduleTime, formatScheduleType, formatTestInterval } from '../tests/formatters/ScheduleFormatters';
import { BusinessServicesCell } from '../tests/formatters/BusinessServicesFormatter';

const RELOAD_INTERVAL = 60000;

const dataSource = {
    url: SERVICE_TESTS_LIST_URL,
    totalProperty: 'count',
    dataProperty: 'data',
    params: {
        searchFields: ['name', 'status', 'tags', 'businessServicesSortable'],
        metrics: ['statusCounts'],
    },
};

const initialSort = [{ property: 'DIVIDE(statusCounts.grayCount, statusCounts.total)', direction: SORT_DESC }];

const useStyles = makeStyles(() => ({
    errorState: {
        color: '#d32f2f',
        fontWeight: 'bold',
    },
}));

function formatError(statusCounts) {
    if (!statusCounts) {
        return '--';
    }
    return `${statusCounts.grayCount}/${statusCounts.total}`;
}

function formatErrorCsv(statusCounts) {
    if (!statusCounts) {
        return '--';
    }
    return `${statusCounts.grayCount} of ${statusCounts.total}`;
}

function formatBusinessServices(bsAry) {
    if (!bsAry || bsAry.length === 0) {
        return '--';
    }
    const names = bsAry.map((bs) => bs.name);
    return names.join(', ');
}

function formatTags(tags) {
    if (!tags || tags.length === 0) {
        return '--';
    }
    return tags.join(', ');
}

function buildTypeListLink(row) {
    const typeGroup = encodeURIComponent(get(row, 'test.testType.group', ''));
    return `${buildRootPageTarget(AdminPages.ServiceTests)}/${typeGroup}/tests?pageState=tests-list`;
}

function buildTestDetailsLink(row) {
    const typeGroup = encodeURIComponent(get(row, 'test.testType.group', ''));
    const testId = encodeURIComponent(get(row, 'test._id', ''));
    return `${buildRootPageTarget(
        AdminPages.ServiceTests
    )}/${typeGroup}/tests/${testId}?returnTo=testTypes.errorDetails`;
}

function buildErrorDetailsLink(row) {
    const testId = encodeURIComponent(get(row, 'test._id', ''));
    return `${buildRootPageTarget(AdminPages.ServiceTests)}/errorDetails/${testId}`;
}

function getColumns(classes) {
    return [
        {
            type: COLUMN_TYPES.ICONLINK,
            mapping: 'test.testType.details.icon',
            title: t`Type`,
            sortProperty: 'test.testType.details.title',
            sortable: true,
            width: 80,
            formatter: (iconValue) => (iconValue ? <Icon>{iconValue}</Icon> : null),
            getTooltip: (row) => get(row, 'test.testType.details.title'),
            link: (row) => ({ target: buildTypeListLink(row) }),
            csvFormatter: (_value, row) => get(row, 'test.testType.details.title'),
        },
        {
            type: COLUMN_TYPES.LINK,
            mapping: 'test.name',
            title: t`Name`,
            sortable: true,
            width: 250,
            link: (row) => ({ target: buildTestDetailsLink(row) }),
        },
        {
            type: COLUMN_TYPES.TEXT,
            mapping: 'test.status',
            title: t`Status`,
            sortable: true,
            width: 100,
        },
        {
            type: COLUMN_TYPES.TEXT,
            mapping: 'test.beaconPackets',
            title: t`Smart Edge Monitoring`,
            sortable: true,
            width: 190,
            renderer: (row) => {
                const beaconPackets = get(row, 'test.beaconPackets');
                return beaconPackets ? 'On' : 'Off';
            },
            csvFormatter: (value) => (value ? 'On' : 'Off'),
        },
        {
            type: COLUMN_TYPES.LINK,
            mapping: 'statusCounts',
            title: t`nPoints in Error State`,
            sortProperty: 'DIVIDE(statusCounts.grayCount, statusCounts.total)',
            sortable: true,
            width: 210,
            formatter: (statusCounts) => {
                const text = formatError(statusCounts);
                const hasErrors = statusCounts && statusCounts.grayCount > 0;
                return <span className={hasErrors ? classes.errorState : undefined}>{text}</span>;
            },
            link: (row) => ({ target: buildErrorDetailsLink(row) }),
            csvFormatter: formatErrorCsv,
        },
        {
            type: COLUMN_TYPES.TEXT,
            mapping: 'test.scheduling',
            title: t`Run Schedule`,
            sortProperty: 'test.scheduling.type',
            sortable: true,
            formatter: formatScheduleType,
            csvFormatter: formatScheduleType,
            width: 120,
        },
        {
            type: COLUMN_TYPES.TEXT,
            mapping: 'test.scheduling',
            title: t`Run Interval`,
            sortProperty: 'test.scheduling.interval',
            sortable: true,
            formatter: formatTestInterval,
            csvFormatter: formatTestInterval,
            width: 115,
        },
        {
            type: COLUMN_TYPES.TEXT,
            mapping: 'test.scheduling',
            title: t`Run Time`,
            sortProperty: 'test.scheduling.start',
            sortable: true,
            formatter: formatScheduleTime,
            csvFormatter: formatScheduleTime,
            width: 200,
        },
        {
            type: COLUMN_TYPES.TEXT,
            mapping: 'test.businessServices',
            title: t`Business Services`,
            sortProperty: 'businessServicesSortable',
            sortable: true,
            width: 210,
            renderer: (row) => {
                const bsAry = get(row, 'test.businessServices');
                return <BusinessServicesCell businessServices={bsAry} />;
            },
            csvFormatter: formatBusinessServices,
        },
        {
            type: COLUMN_TYPES.TEXT,
            mapping: 'test.tags',
            title: t`Tags`,
            width: 300,
            renderer: (row) => {
                const tags = get(row, 'test.tags');
                return formatTags(tags);
            },
            csvFormatter: formatTags,
            flex: 1,
        },
    ];
}

export default function TestErrorListPanel({ onSelectionChange, onTableStateChange }) {
    const classes = useStyles();
    const loader = useQueryLoader(dataSource, { disableFilterState: true });
    const { triggerRef } = useIntervalRefreshTrigger({ interval: RELOAD_INTERVAL });
    const columns = useMemo(() => getColumns(classes), [classes]);

    return (
        <DataTable
            tableId="tests-errors-list"
            loader={loader}
            triggerRef={triggerRef}
            columns={columns}
            visibleRows={0}
            showSelectionColumn
            disableColumnRemover
            sorters={initialSort}
            onStateChanged={onTableStateChange}
            selectionOptions={{
                mode: SELECTIONMODE_MULTI,
                allowSelectAll: true,
                idMapping: 'test._id',
                onChange: onSelectionChange,
            }}
        />
    );
}
